// src/pages/MacchineCliente.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { Badge, Card } from '../components/ui';

const API_BASE = '/api';

export default function MacchineCliente() {
  const navigate = useNavigate();

  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('role');
    localStorage.removeItem('profilePicture');
    localStorage.removeItem('mustChangePassword');
    navigate('/login');
  };

  const loadMachines = async () => {
    try {
      setLoading(true);
      setError('');

      const res = await authFetch(`${API_BASE}/machines/mine`);

      if (!res.ok) {
        if (res.status === 401) {
          logout();
          return;
        }

        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message || 'Errore nel recupero macchine');
      }

      const data = await res.json();
      setMachines(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Errore loadMachines:', err);
      setError(err?.message || 'Impossibile recuperare le macchine.');
      setMachines([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMachines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const getPairingBadge = (status) => {
    switch (status) {
      case 'paired':
        return {
          label: 'Associato',
          className: 'bg-emerald-100 text-emerald-700',
        };
      case 'provisioning':
        return {
          label: 'In riconfigurazione',
          className: 'bg-amber-100 text-amber-700',
        };
      case 'unpaired':
        return {
          label: 'Disassociato',
          className: 'bg-slate-100 text-slate-700',
        };
      case 'error':
        return {
          label: 'Errore',
          className: 'bg-red-100 text-red-700',
        };
      case 'never_paired':
      default:
        return {
          label: 'Mai associato',
          className: 'bg-gray-100 text-gray-600',
        };
    }
  };



  const filteredMachines = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return machines;

    return machines.filter((machine) => {
      const devices = Array.isArray(machine.devices) ? machine.devices : [];
      const haystack = [
        machine.nome,
        machine.sede,
        machine.reparto,
        machine.matricola,
        machine.note,
        ...devices.map((d) => d?.device_id),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [machines, q]);

  const formatDateTime = (d) => {
    if (!d) return '-';
    try {
      return new Date(d).toLocaleString();
    } catch {
      return '-';
    }
  };

  const getSmartState = (devices = []) => {
    if (!devices.length) {
      return {
        label: 'Nessun device',
        className: 'bg-gray-100 text-gray-600',
      };
    }

    const firstDevice = devices[0];
    const pairingStatus = firstDevice?.pairing_status || '';
    const latestSeen = firstDevice?.last_seen_at
      ? new Date(firstDevice.last_seen_at).getTime()
      : 0;

    if (pairingStatus === 'provisioning') {
      return {
        label: 'Riconfigurazione in corso',
        className: 'bg-amber-100 text-amber-700',
      };
    }

    if (pairingStatus === 'unpaired') {
      return {
        label: 'Device disassociato',
        className: 'bg-slate-100 text-slate-700',
      };
    }

    if (pairingStatus === 'error') {
      return {
        label: 'Errore device',
        className: 'bg-red-100 text-red-700',
      };
    }

    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;

    if (latestSeen && now - latestSeen < oneDay) {
      return {
        label: 'Smart attivo',
        className: 'bg-emerald-100 text-emerald-700',
      };
    }

    return {
      label: 'Smart da verificare',
      className: 'bg-amber-100 text-amber-700',
    };
  };



const renderMeasures = (devices = []) => {
  const firstMeasuresDevice = devices.find(
    (d) => Array.isArray(d?.last_measures) && d.last_measures.length > 0
  );

  // Temperature (PT100 e sonde del bus) e umidita' HUM-* del Pulse, in ordine.
  const sortKey = (sensorId = '') =>
    (String(sensorId).startsWith('HUM-') ? 1000 : 0) +
    (Number(String(sensorId).replace(/\D/g, '')) || 0);

  const measures = Array.isArray(firstMeasuresDevice?.last_measures)
    ? firstMeasuresDevice.last_measures
        .filter((measure) => /^(TEMP|HUM)-\d+$/.test(measure?.sensor_id || ''))
        .sort((a, b) => sortKey(a?.sensor_id) - sortKey(b?.sensor_id))
    : [];

  if (!measures.length) {
    return (
      <p className="text-sm text-slate-500">
        Nessun sensore rilevato al momento.
      </p>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {measures.map((measure, index) => {
        const safe = measure || {};
        return (
          <div
            key={`${safe.sensor_id || 'sensor'}-${index}`}
            className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3"
          >
            <p className="text-sm font-semibold text-slate-800">
              {safe.sensor_id || 'Sensore'}
            </p>
            <p className="mt-1 text-sm text-slate-600">
              Tipo: {safe.sensor_type || '-'}
            </p>
            <p className="mt-1 text-sm text-slate-600">
              Valore:{' '}
              {safe.value !== undefined && safe.value !== null
                ? String(safe.value)
                : '-'}{' '}
              {safe.unit === 'C' ? '°C' : safe.unit || ''}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Qualità: {safe.quality_flag || '-'}
            </p>
          </div>
        );
      })}
    </div>
  );
};

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 via-gray-100 to-white pb-12">
      <div className="max-w-7xl mx-auto px-4 pt-10 space-y-6">
        <section className="rounded-3xl border border-slate-200 bg-white shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-sky-50 via-white to-cyan-50 px-6 py-8 md:px-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="max-w-3xl">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold uppercase tracking-wide">
                  Macchine e monitoraggio
                </div>

                <h1 className="mt-4 text-3xl md:text-4xl font-bold text-slate-800">
                  Le tue macchine installate
                </h1>

                <p className="mt-3 text-slate-600 text-sm md:text-base">
                  Consulta i tuoi impianti, verifica i dispositivi smart associati
                  e controlla i sensori disponibili in un colpo d’occhio.
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={loadMachines}
                  className="px-4 py-2.5 bg-slate-800 text-white rounded-xl hover:bg-slate-900 text-sm font-semibold transition"
                >
                  Aggiorna
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/dashboard-cliente')}
                  className="px-4 py-2.5 bg-white text-slate-700 border border-slate-300 rounded-xl hover:bg-slate-50 text-sm font-semibold transition"
                >
                  Torna alla dashboard
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Cerca macchina o device
              </label>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Nome macchina, matricola, sede, device..."
                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>

            <div className="text-sm text-slate-500">
              Totale macchine: <strong className="text-slate-800">{filteredMachines.length}</strong>
            </div>
          </div>
        </section>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm shadow-sm">
            {error}
          </div>
        )}

        {loading ? (
          <Card className="px-6 py-16 text-center shadow-sm">
            <p className="text-slate-500 animate-pulse">
              Caricamento macchine cliente...
            </p>
          </Card>
        ) : filteredMachines.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center shadow-sm">
            <p className="text-slate-500">
              Nessuna macchina associata al tuo account.
            </p>
          </div>
        ) : (
          <div className="grid gap-6">
            {filteredMachines.map((machine) => {
              const devices = Array.isArray(machine.devices) ? machine.devices : [];
              const smartState = getSmartState(devices);
              const latestSeen = devices[0]?.last_seen_at || null;

              return (
                <section
                  key={machine._id}
                  className="rounded-3xl border border-slate-200 bg-white p-6 shadow-md"
                >
                  <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-6">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-3">
                        <h2 className="text-2xl font-bold text-slate-800">
                          {machine.nome || 'Macchina'}
                        </h2>

                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${smartState.className}`}
                        >
                          {smartState.label}
                        </span>

                        <Badge className="bg-slate-100 text-slate-700">
                          {machine.matricola || 'Senza matricola'}
                        </Badge>
                      </div>

                      <p className="mt-3 text-sm text-slate-600">
                        {machine.sede || '-'} · {machine.reparto || '-'}
                      </p>

                      {machine.note && (
                        <p className="mt-2 text-sm text-slate-500">
                          {machine.note}
                        </p>
                      )}
                    </div>

                    <div className="grid gap-3 sm:grid-cols-3 xl:min-w-[360px]">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs uppercase tracking-wide text-slate-400">
                          Device associati
                        </p>
                        <p className="mt-2 text-2xl font-bold text-slate-800">
                          {devices.length}
                        </p>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs uppercase tracking-wide text-slate-400">
                          Ultimo contatto
                        </p>
                        <p className="mt-2 text-sm font-semibold text-slate-800">
                          {formatDateTime(latestSeen)}
                        </p>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs uppercase tracking-wide text-slate-400">
                          Stato pairing
                        </p>
                        {(() => {
                          const pairingBadge = getPairingBadge(devices[0]?.pairing_status);
                          return (
                            <span
                              className={`mt-2 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${pairingBadge.className}`}
                            >
                              {pairingBadge.label}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 grid gap-6 xl:grid-cols-[1fr_1.2fr]">
                    <div className="rounded-2xl border border-slate-200 bg-white">
                      <div className="border-b border-slate-200 px-4 py-3">
                        <h3 className="text-sm font-semibold text-slate-800">
                          Dispositivi smart associati
                        </h3>
                      </div>

                      <div className="p-4">
                        {devices.length === 0 ? (
                          <p className="text-sm text-slate-500">
                            Nessun dispositivo smart associato a questa macchina.
                          </p>
                        ) : (
                          <div className="space-y-3">
                            {devices.map((device) => (
                              <div
                                key={device._id}
                                className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3"
                              >
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                  <div>
                                    <p className="text-sm font-semibold text-slate-800">
                                      {device.device_id}
                                    </p>
                                    <p className="mt-1 text-xs text-slate-500">
                                      IP: {device.ip_address || '-'}
                                    </p>
                                  </div>

                                  <div className="text-xs text-slate-500">
                                    Ultimo contatto: {formatDateTime(device.last_seen_at)}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white">
                      <div className="border-b border-slate-200 px-4 py-3">
                        <h3 className="text-sm font-semibold text-slate-800">
                          Sensori rilevati
                        </h3>
                      </div>

                      <div className="p-4">
                        {renderMeasures(devices)}
                      </div>
                    </div>
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}